import { useQuery, UseQueryOptions } from '@tanstack/react-query';

/**
 * Custom hook for optimized data fetching with intelligent caching
 * Automatically configures stale times based on data type
 */

type DataType = 'static' | 'semi-static' | 'dynamic' | 'real-time';

interface OptimizedQueryOptions<TData> extends Omit<UseQueryOptions<TData>, 'staleTime'> {
  dataType?: DataType;
  customStaleTime?: number;
}

/**
 * Stale time configurations based on data update frequency
 * - static: Course data, user profiles (changes rarely) - 10 minutes
 * - semi-static: Enrollments, progress (changes moderately) - 5 minutes
 * - dynamic: Notifications, activities (changes frequently) - 2 minutes
 * - real-time: Live data, analytics (changes constantly) - 30 seconds
 */
const STALE_TIME_CONFIG: Record<DataType, number> = {
  'static': 10 * 60 * 1000,      // 10 minutes
  'semi-static': 5 * 60 * 1000,  // 5 minutes
  'dynamic': 2 * 60 * 1000,      // 2 minutes
  'real-time': 30 * 1000,        // 30 seconds
};

export function useOptimizedQuery<TData>(
  options: OptimizedQueryOptions<TData>
) {
  const { dataType = 'dynamic', customStaleTime, ...queryOptions } = options;
  
  const staleTime = customStaleTime ?? STALE_TIME_CONFIG[dataType];
  
  return useQuery<TData>({
    ...queryOptions,
    staleTime,
    gcTime: staleTime * 2, // Keep cached data for twice the stale time
    refetchOnWindowFocus: dataType === 'real-time', // Only refetch real-time data on focus
    refetchOnReconnect: true,
  });
}
